import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import {
  Button,
  Input,
  Textarea,
  Select,
  Card,
  FileUpload,
} from "../components/ui";

// Ruoli che possono selezionare fornitore
const RUOLI_ADMIN = [100000000, 100000001, 100000002]; // Admin, RSPP, Direzione

function NuovoInterventoPage() {
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [rsaList, setRsaList] = useState([]);
  const [fornitoriList, setFornitoriList] = useState([]);
  const [fornitoriRsa, setFornitoriRsa] = useState([]); // Fornitori filtrati per RSA selezionata
  const [loadingFornitori, setLoadingFornitori] = useState(false);
  const [tipologieList, setTipologieList] = useState([]);
  const [tipologieFornitore, setTipologieFornitore] = useState([]); // Tipologie del fornitore selezionato
  const [tipologieSelezionate, setTipologieSelezionate] = useState([]); // Tipologie selezionate (multi)
  const [scadenzeAggiuntiveSelezionate, setScadenzeAggiuntiveSelezionate] =
    useState([]); // Scadenze da chiudere insieme, oltre alla prima selezionata (N:N)
  const [contrattiList, setContrattiList] = useState([]);
  const [errors, setErrors] = useState({});
  const [scadenzeNonCollegate, setScadenzeNonCollegate] = useState([]); // Warning scadenze aperte non collegate

  // Step 1: upload rapportino (con preview) - Step 2: form completo
  const [step, setStep] = useState("upload");
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Verifica se utente puo selezionare fornitore
  const isAdmin = RUOLI_ADMIN.includes(user?.ruolo);
  const isFornitore = user?.ruolo === 100000005;

  const [formData, setFormData] = useState({
    nome: "",
    dataIntervento: new Date().toISOString().split("T")[0],
    rsaId: searchParams.get("rsaId") || "",
    fornitoreId: "",
    tipologiaInterventoId: searchParams.get("tipologiaId") || "",
    scadenzaId: searchParams.get("scadenzaId") || "",
    richiestaId: searchParams.get("richiestaId") || "",
    contrattoId: "",
    esito: "", // Obbligatorio - l'utente deve scegliere
    tipoManutenzione: "100000000", // Ordinaria di default
    oreLavorate: "",
    costoIntervento: "",
    nomeTecnico: "",
    descrizione: "",
    materialiUtilizzati: "",
    rapportini: [], // Array per supportare upload multipli
  });

  // Esito opzioni (OptionSet Dataverse)
  const esitoOptions = [
    { value: "100000000", label: "Positivo" },
    { value: "100000001", label: "Negativo" },
  ];

  // Tipo Manutenzione opzioni (OptionSet Dataverse)
  const tipoManutenzioneOptions = [
    { value: "100000000", label: "Manutenzione Ordinaria" },
    { value: "100000001", label: "Manutenzione Straordinaria" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requests = [
          apiRequest("/rsa"),
          apiRequest("/tipologie-intervento").catch(() => ({ value: [] })),
        ];

        // Solo admin carica lista fornitori
        if (isAdmin) {
          requests.push(apiRequest("/fornitori").catch(() => ({ value: [] })));
        }

        const results = await Promise.all(requests);

        setRsaList(results[0].value || []);
        setTipologieList(results[1].value || []);
        if (isAdmin && results[2]) {
          setFornitoriList(results[2].value || []);
        }

        // Se arriva da una scadenza, carica i dettagli e pre-compila il form
        if (searchParams.get("scadenzaId")) {
          try {
            const scadenza = await apiRequest(
              `/scadenze/${searchParams.get("scadenzaId")}`,
            );
            const rsaNome = scadenza.rsa?.nome || "";
            const tipologiaNome =
              scadenza.tipologiaIntervento || "Manutenzione";

            setFormData((prev) => ({
              ...prev,
              nome: `${tipologiaNome} - ${rsaNome}`,
              rsaId: scadenza.rsa?.id || scadenza.rsaId || prev.rsaId,
              tipologiaInterventoId:
                scadenza.tipologiaInterventoId || prev.tipologiaInterventoId,
              fornitoreId: scadenza.fornitore?.id || scadenza.fornitoreId || "",
              scadenzaId: searchParams.get("scadenzaId"),
              // La data intervento e oggi di default (l'utente deve confermare o modificare)
              dataIntervento: new Date().toISOString().split("T")[0],
            }));
            // Fase 4 piano unificazione tipologia (validated-wandering-fox.md):
            // popola anche l'array N:N, canale ora autoritativo lato backend.
            if (scadenza.tipologiaInterventoId) {
              setTipologieSelezionate((prev) =>
                prev.length > 0 ? prev : [scadenza.tipologiaInterventoId],
              );
            }
          } catch (e) {
            console.error("Error loading scadenza:", e);
            showToast("Errore nel caricamento della scadenza", "error");
          }
        }

        // Se arriva da una richiesta di intervento, pre-compila il form
        if (searchParams.get("richiestaId")) {
          try {
            const richiesta = await apiRequest(
              `/richieste-intervento/${searchParams.get("richiestaId")}`,
            );
            const rsaNome = richiesta.rsa?.nome || "";
            setFormData((prev) => ({
              ...prev,
              nome: richiesta.oggetto || `Intervento - ${rsaNome}`,
              rsaId: richiesta.rsa?.id || prev.rsaId,
              fornitoreId: richiesta.fornitore?.id || "",
              tipologiaInterventoId:
                richiesta.tipologiaIntervento?.id || prev.tipologiaInterventoId,
              richiestaId: searchParams.get("richiestaId"),
              tipoManutenzione:
                richiesta.tipologiaRichiesta === 100000001
                  ? "100000001"
                  : "100000000",
              dataIntervento: new Date().toISOString().split("T")[0],
            }));
            // Fase 4 piano unificazione tipologia (validated-wandering-fox.md):
            // popola anche l'array N:N, canale ora autoritativo lato backend.
            if (richiesta.tipologiaIntervento?.id) {
              setTipologieSelezionate((prev) =>
                prev.length > 0 ? prev : [richiesta.tipologiaIntervento.id],
              );
            }
          } catch (e) {
            console.error("Error loading richiesta:", e);
            showToast("Errore nel caricamento della richiesta", "error");
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
        showToast("Errore nel caricamento dei dati", "error");
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [apiRequest, showToast, isAdmin, searchParams]);

  // Carica contratti quando viene selezionata una RSA
  useEffect(() => {
    if (!formData.rsaId) {
      setContrattiList([]);
      return;
    }

    const fetchContratti = async () => {
      try {
        const contrattiRes = await apiRequest(
          `/contratti?rsaId=${formData.rsaId}&attivo=true`,
        ).catch(() => ({ value: [] }));
        setContrattiList(contrattiRes.value || []);
      } catch (error) {
        console.error("Error loading contratti:", error);
      }
    };

    fetchContratti();
  }, [formData.rsaId, apiRequest]);

  // Carica fornitori associati alla RSA selezionata (solo per admin)
  useEffect(() => {
    if (!isAdmin) return;

    if (!formData.rsaId) {
      setFornitoriRsa([]);
      return;
    }

    const fetchFornitoriRsa = async () => {
      setLoadingFornitori(true);
      try {
        // Prova a caricare fornitori associati alla RSA via N:N
        const res = await apiRequest(`/rsa/${formData.rsaId}/fornitori`);
        if (res.value && res.value.length > 0) {
          setFornitoriRsa(res.value);
        } else {
          // Fallback: mostra tutti i fornitori se la RSA non ha associazioni
          setFornitoriRsa(fornitoriList);
        }
      } catch (error) {
        console.log("Fornitori RSA non disponibili, uso tutti:", error.message);
        // Fallback: mostra tutti i fornitori
        setFornitoriRsa(fornitoriList);
      } finally {
        setLoadingFornitori(false);
      }
    };

    fetchFornitoriRsa();
  }, [formData.rsaId, isAdmin, apiRequest, fornitoriList]);

  // Carica tipologie del fornitore selezionato (o del fornitore loggato)
  useEffect(() => {
    const fornitoreIdToLoad = isFornitore
      ? user?.fornitore?.id
      : formData.fornitoreId;

    if (!fornitoreIdToLoad) {
      setTipologieFornitore([]);
      setTipologieSelezionate([]);
      return;
    }

    const fetchTipologieFornitore = async () => {
      try {
        const result = await apiRequest(
          `/fornitori/${fornitoreIdToLoad}/tipologie-intervento`,
        );
        setTipologieFornitore(result.value || []);
      } catch (error) {
        console.error("Error loading tipologie fornitore:", error);
        setTipologieFornitore([]);
      }
    };

    fetchTipologieFornitore();
  }, [formData.fornitoreId, isFornitore, user?.fornitore?.id, apiRequest]);

  // Carica le scadenze aperte collegabili all'intervento (RSA + fornitore)
  useEffect(() => {
    const fornitoreIdToCheck = isFornitore
      ? user?.fornitore?.id
      : formData.fornitoreId;

    if (!formData.rsaId || !fornitoreIdToCheck) {
      setScadenzeNonCollegate([]);
      return;
    }

    const checkScadenze = async () => {
      try {
        const params = new URLSearchParams({
          rsaId: formData.rsaId,
          fornitoreId: fornitoreIdToCheck,
        });
        if (formData.tipologiaInterventoId) {
          params.append(
            "tipologiaInterventoId",
            formData.tipologiaInterventoId,
          );
        }
        const result = await apiRequest(
          `/interventi/check-scadenze-aperte?${params.toString()}`,
        );
        setScadenzeNonCollegate(result.scadenzeAperte || []);
      } catch {
        setScadenzeNonCollegate([]);
      }
    };

    checkScadenze();
  }, [
    formData.rsaId,
    formData.fornitoreId,
    formData.tipologiaInterventoId,
    isFornitore,
    user?.fornitore?.id,
    apiRequest,
  ]);

  // Filtra fornitori per RSA e poi per tipologia se selezionata, ordinati alfabeticamente
  const filteredFornitori = (
    formData.tipologiaInterventoId
      ? fornitoriRsa.filter(
          (f) =>
            f.tipologie?.some((t) => t.id === formData.tipologiaInterventoId) ||
            !f.tipologie,
        )
      : fornitoriRsa
  ).sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "it"));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    // Reset fornitore e tipologie quando cambia RSA
    if (name === "rsaId") {
      setFormData((prev) => ({
        ...prev,
        rsaId: value,
        fornitoreId: "",
        scadenzaId: "",
      }));
      setTipologieSelezionate([]);
      setScadenzeAggiuntiveSelezionate([]);
      // Non resettare tipologieFornitore per Fornitore: sono legate al fornitore, non alla RSA
      if (!isFornitore) {
        setTipologieFornitore([]);
      }
    }

    // Reset tipologie e scadenze selezionate quando cambia fornitore
    if (name === "fornitoreId") {
      setTipologieSelezionate([]);
      setScadenzeAggiuntiveSelezionate([]);
      setFormData((prev) => ({ ...prev, scadenzaId: "" }));
    }

    // Auto-genera nome se cambia tipologia
    if (name === "tipologiaInterventoId" && value) {
      const tipologia = tipologieList.find((t) => t.id === value);
      if (tipologia && !formData.nome) {
        setFormData((prev) => ({
          ...prev,
          nome: `Intervento: ${tipologia.nome}`,
        }));
      }
    }
  };

  const handleFileChange = (files) => {
    // FileUpload con multiple=true restituisce un array
    setFormData((prev) => ({
      ...prev,
      rapportini: Array.isArray(files) ? files : files ? [files] : [],
    }));
  };

  // Solo i PDF hanno una preview (immagini/zip restano solo nella lista upload)
  const pdfFiles = formData.rapportini.filter(
    (f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name || ""),
  );
  const safePreviewIndex = Math.min(
    activePreviewIndex,
    Math.max(pdfFiles.length - 1, 0),
  );
  const activePreviewFile = pdfFiles[safePreviewIndex] || null;

  useEffect(() => {
    if (!activePreviewFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(activePreviewFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [activePreviewFile]);

  const handleTipologiaToggle = (tipologiaId) => {
    setTipologieSelezionate((prev) => {
      if (prev.includes(tipologiaId)) {
        return prev.filter((id) => id !== tipologiaId);
      } else {
        return [...prev, tipologiaId];
      }
    });
  };

  // Scadenze aperte selezionate per la chiusura (nessuna gerarchia visibile
  // all'utente: la prima selezionata diventa la primaria lato dati, il resto
  // le aggiuntive N:N — la distinzione è puramente interna)
  const scadenzeSelezionateIds = formData.scadenzaId
    ? [formData.scadenzaId, ...scadenzeAggiuntiveSelezionate]
    : scadenzeAggiuntiveSelezionate;

  const handleToggleScadenza = (scadenza) => {
    const isSelected = scadenzeSelezionateIds.includes(scadenza.id);
    const nuove = isSelected
      ? scadenzeSelezionateIds.filter((sid) => sid !== scadenza.id)
      : [...scadenzeSelezionateIds, scadenza.id];
    const [primaria, ...extra] = nuove;

    setScadenzeAggiuntiveSelezionate(extra);
    setFormData((prev) => {
      const updates = { ...prev, scadenzaId: primaria || "" };
      if (!isSelected && !prev.scadenzaId && !prev.nome) {
        const rsaNome = rsaList.find((r) => r.id === prev.rsaId)?.nome || "";
        updates.nome =
          `${scadenza.tipologiaIntervento || scadenza.nome || "Manutenzione"} - ${rsaNome}`.trim();
      }
      return updates;
    });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = "Il nome e obbligatorio";
    }
    if (!formData.dataIntervento) {
      newErrors.dataIntervento =
        "La data intervento e obbligatoria - indica quando e stato eseguito l'intervento";
    } else {
      // Verifica che la data non sia nel futuro
      const dataIntervento = new Date(formData.dataIntervento);
      const oggi = new Date();
      oggi.setHours(23, 59, 59, 999);
      if (dataIntervento > oggi) {
        newErrors.dataIntervento =
          "La data intervento non puo essere nel futuro";
      }
    }
    if (!formData.rsaId) {
      newErrors.rsaId = "Seleziona una RSA";
    }
    if (isAdmin && !formData.fornitoreId) {
      newErrors.fornitoreId = "Seleziona un fornitore";
    }
    if (!formData.esito) {
      newErrors.esito = "Seleziona l'esito dell'intervento";
    }
    if (!formData.descrizione.trim()) {
      newErrors.descrizione = "La descrizione e obbligatoria";
    }
    if (formData.oreLavorate && isNaN(Number(formData.oreLavorate))) {
      newErrors.oreLavorate = "Inserisci un numero valido";
    }
    if (formData.costoIntervento && isNaN(Number(formData.costoIntervento))) {
      newErrors.costoIntervento = "Inserisci un importo valido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showToast("Correggi gli errori nel form", "warning");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        nome: formData.nome,
        dataIntervento: formData.dataIntervento,
        rsaId: formData.rsaId,
        esito: parseInt(formData.esito),
        tipoManutenzione: parseInt(formData.tipoManutenzione),
        descrizione: formData.descrizione,
      };

      // Fornitore (obbligatorio per admin)
      if (formData.fornitoreId) {
        payload.fornitoreId = formData.fornitoreId;
      }

      // Optional fields
      if (formData.tipologiaInterventoId) {
        payload.tipologiaInterventoId = formData.tipologiaInterventoId;
      }
      if (formData.scadenzaId) {
        payload.scadenzaId = formData.scadenzaId;
      }
      if (formData.richiestaId) {
        payload.richiestaId = formData.richiestaId;
      }
      if (formData.contrattoId) {
        payload.contrattoId = formData.contrattoId;
      }
      if (formData.oreLavorate) {
        payload.oreLavorate = parseFloat(formData.oreLavorate);
      }
      if (formData.costoIntervento) {
        payload.costoIntervento = parseFloat(formData.costoIntervento);
      }
      if (formData.nomeTecnico) {
        payload.nomeTecnico = formData.nomeTecnico;
      }
      if (formData.materialiUtilizzati) {
        payload.materialiUtilizzati = formData.materialiUtilizzati;
      }

      // Tipologie e scadenze aggiuntive viaggiano con l'intervento: il backend le
      // associa nella stessa operazione di creazione
      if (tipologieSelezionate.length > 0) {
        payload.tipologiaIds = tipologieSelezionate;
      }
      if (scadenzeAggiuntiveSelezionate.length > 0) {
        payload.scadenzeAggiuntiveIds = scadenzeAggiuntiveSelezionate;
      }

      // Crea intervento
      const result = await apiRequest("/interventi", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      // Upload rapportini se presenti (supporto per multipli PDF)
      if (formData.rapportini.length > 0 && result.id) {
        let uploadSuccessCount = 0;
        let uploadErrorCount = 0;

        for (let i = 0; i < formData.rapportini.length; i++) {
          const file = formData.rapportini[i];
          try {
            const formDataUpload = new FormData();
            formDataUpload.append("file", file);
            // Nome con indice se multipli file
            const nomeDoc =
              formData.rapportini.length > 1
                ? `Rapportino ${i + 1} - ${formData.nome}`
                : `Rapportino - ${formData.nome}`;
            formDataUpload.append("nome", nomeDoc);
            formDataUpload.append("tipoDocumento", "100000003"); // Rapportini
            formDataUpload.append("rsaId", formData.rsaId);
            formDataUpload.append("registroId", result.id);
            if (formData.fornitoreId) {
              formDataUpload.append("fornitoreId", formData.fornitoreId);
            }

            await apiRequest("/documenti/upload", {
              method: "POST",
              body: formDataUpload,
              headers: {}, // Remove Content-Type to let browser set it with boundary
            });
            uploadSuccessCount++;
          } catch (uploadError) {
            console.error(`Error uploading rapportino ${i + 1}:`, uploadError);
            uploadErrorCount++;
          }
        }

        // Feedback all'utente
        if (uploadErrorCount > 0 && uploadSuccessCount > 0) {
          showToast(
            `Intervento salvato. ${uploadSuccessCount} rapportino/i caricato/i, ${uploadErrorCount} errore/i`,
            "warning",
          );
        } else if (uploadErrorCount > 0 && uploadSuccessCount === 0) {
          showToast(
            "Intervento salvato, ma errore nel caricamento dei rapportini",
            "warning",
          );
        }
      }

      let message = isFornitore
        ? "Intervento registrato con successo! In attesa di validazione."
        : "Intervento registrato con successo!";

      // Notifica se il backend ha auto-collegato una scadenza
      if (result.autoMatched) {
        message += " Una scadenza aperta e stata collegata automaticamente.";
      }

      showToast(message, "success");
      navigate("/interventi");
    } catch (error) {
      console.error("Error creating intervento:", error);
      showToast(error.message || "Errore nella registrazione", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div
      className={
        step === "form" && pdfFiles.length > 0 ? "w-full" : "max-w-3xl mx-auto"
      }
    >
      {/* Breadcrumb */}
      <div className="mb-2 text-sm text-gray-500">
        <a href="/interventi" className="hover:text-primary-600">
          Interventi
        </a>
        <span className="mx-2">/</span>
        <span>Nuovo</span>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Nuovo Intervento</h1>
        <p className="text-gray-600 mt-1">
          {isFornitore
            ? "Registra un nuovo intervento di manutenzione. L'intervento sara inviato per validazione."
            : "Registra un nuovo intervento di manutenzione."}
        </p>
      </div>

      {step === "upload" && (
        <Card className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Carica il rapportino</h2>
          <p className="text-sm text-gray-600 mb-4">
            Carica il PDF del rapportino di intervento: potrai vederlo in
            anteprima a fianco mentre compili i dati, senza dover passare da una
            scheda all'altra.
          </p>

          <FileUpload
            accept=".pdf,.jpg,.jpeg,.png,.zip,.rar"
            maxSize={100}
            multiple={true}
            value={formData.rapportini}
            onChange={handleFileChange}
            helper="PDF, immagini, ZIP, RAR - max 100MB ciascuno"
          />

          <div className="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep("form")}
            >
              Continua senza documento
            </Button>
            <Button
              type="button"
              onClick={() => setStep("form")}
              disabled={formData.rapportini.length === 0}
            >
              Continua
            </Button>
          </div>
        </Card>
      )}

      {step === "form" && (
        <div
          className={
            pdfFiles.length > 0
              ? "grid grid-cols-1 lg:grid-cols-2 gap-6 items-start"
              : ""
          }
        >
          {pdfFiles.length > 0 && (
            <div className="lg:sticky lg:top-4 lg:self-start">
              <div className="bg-white rounded-lg shadow flex flex-col h-[50vh] lg:h-[calc(100vh-8rem)] overflow-hidden">
                {pdfFiles.length > 1 && (
                  <div className="flex gap-1 overflow-x-auto border-b border-gray-200 bg-gray-50 px-2 py-2 flex-shrink-0">
                    {pdfFiles.map((file, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setActivePreviewIndex(idx)}
                        title={file.name}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
                          idx === safePreviewIndex
                            ? "bg-primary-600 text-white"
                            : "text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {file.name.length > 22
                          ? `${file.name.slice(0, 20)}…`
                          : file.name}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex-1 min-h-0">
                  {previewUrl ? (
                    <iframe
                      src={previewUrl}
                      title="Anteprima rapportino"
                      className="w-full h-full"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-sm text-gray-400">
                      Caricamento anteprima...
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Usa i controlli del visualizzatore PDF per scorrere le pagine e
                zoomare.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Banner se sta completando una scadenza */}
            {formData.scadenzaId && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📅</span>
                  <div>
                    <h3 className="font-medium text-green-900">
                      Stai registrando un intervento per una scadenza
                      programmata
                    </h3>
                    <p className="text-sm text-green-700 mt-1">
                      Dopo la validazione, questa scadenza sara marcata come
                      completata e verra automaticamente creata la prossima
                      scadenza basata sulla{" "}
                      <strong>data dell'intervento</strong> che inserisci.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Selezione Scadenza - Collegamento a manutenzione programmata */}
            {(isFornitore ||
              scadenzeNonCollegate.length > 0 ||
              (isAdmin && formData.fornitoreId)) && (
              <Card className="mb-6 border-2 border-primary-400 bg-primary-100">
                <h2 className="text-lg font-semibold mb-2 flex items-center gap-2 text-primary-900">
                  <span>📋</span> Collegamento a Manutenzione Programmata
                </h2>
                <div className="text-sm text-gray-600 mb-4 space-y-2">
                  <p>
                    <strong>
                      Hai eseguito un intervento gia previsto a calendario?
                    </strong>{" "}
                    Seleziona qui sotto la o le scadenze corrispondenti per
                    registrare il completamento della manutenzione programmata
                    (più di una se questo rapportino le chiude tutte, es. un
                    unico sopralluogo con più verifiche).
                  </p>
                  <p className="text-gray-500">
                    Dopo la validazione, le scadenze selezionate verranno chiuse
                    automaticamente e il sistema creera le prossime scadenze in
                    base alla frequenza prevista. Se l'intervento{" "}
                    <strong>non</strong> e collegato a nessuna scadenza, non
                    selezionarne nessuna.
                  </p>
                </div>

                {scadenzeNonCollegate.length > 0 ? (
                  <div className="space-y-2">
                    {scadenzeNonCollegate.map((s) => {
                      const selezionata = scadenzeSelezionateIds.includes(s.id);
                      const label = `${s.tipologiaIntervento || s.nome} - entro il ${new Date(s.dataScadenza).toLocaleDateString("it-IT")}`;

                      // Con una sola scadenza aperta basta un bottone, senza
                      // checkbox: non c'e nulla da selezionare tra più voci.
                      if (scadenzeNonCollegate.length === 1) {
                        return (
                          <div
                            key={s.id}
                            className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${
                              selezionata
                                ? "border-primary-500 bg-white"
                                : "border-primary-200 bg-white"
                            }`}
                          >
                            <span className="text-sm text-gray-800">
                              {label}
                              {s.stato === 100000002 && (
                                <span className="ml-1 text-red-600 font-semibold">
                                  (SCADUTA)
                                </span>
                              )}
                            </span>
                            <Button
                              type="button"
                              variant={selezionata ? "outline" : "primary"}
                              onClick={() => handleToggleScadenza(s)}
                            >
                              {selezionata ? "Scollega" : "Collega a scadenza"}
                            </Button>
                          </div>
                        );
                      }

                      return (
                        <label
                          key={s.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selezionata
                              ? "border-primary-500 bg-white"
                              : "border-primary-200 bg-white hover:border-primary-300"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selezionata}
                            onChange={() => handleToggleScadenza(s)}
                            className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-800 flex-1">
                            {label}
                            {s.stato === 100000002 && (
                              <span className="ml-1 text-red-600 font-semibold">
                                (SCADUTA)
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                    {scadenzeSelezionateIds.length > 0 && (
                      <p className="text-sm text-primary-600">
                        {scadenzeSelezionateIds.length} scadenza/e selezionata/e
                        da chiudere
                      </p>
                    )}
                    <p className="text-xs text-gray-400">
                      Sono mostrate solo le scadenze ancora aperte
                      {isFornitore
                        ? " assegnate a te"
                        : formData.fornitoreId
                          ? " assegnate a questo fornitore"
                          : ""}
                      .
                    </p>
                  </div>
                ) : !formData.rsaId ? (
                  <p className="text-sm text-gray-500 italic">
                    Seleziona prima una RSA per vedere le scadenze disponibili.
                  </p>
                ) : isAdmin && !formData.fornitoreId ? (
                  <p className="text-sm text-gray-500 italic">
                    Seleziona prima un fornitore per vedere le scadenze
                    disponibili.
                  </p>
                ) : isFornitore ? (
                  <p className="text-sm text-gray-500 italic">
                    Non hai scadenze aperte per questa RSA. Puoi comunque
                    registrare un intervento non collegato a scadenza.
                  </p>
                ) : (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <div className="flex items-start gap-3">
                      <svg
                        className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900">
                          Nessuna scadenza aperta
                        </p>
                        <p className="mt-1 text-sm text-blue-800">
                          Non risultano scadenze in sospeso assegnate a questo
                          fornitore per la RSA selezionata.
                        </p>
                        {tipologieFornitore.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-blue-700">
                              Tipologie intervento ricercate:
                            </p>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {tipologieFornitore.map((t) => (
                                <span
                                  key={t.id}
                                  className="inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800"
                                >
                                  {t.nome}
                                  {t.frequenzaValore && (
                                    <span className="ml-1 text-blue-500">
                                      · ogni {t.frequenzaValore}{" "}
                                      {t.frequenzaUnita === 100000000
                                        ? "gg"
                                        : t.frequenzaUnita === 100000001
                                          ? "sett"
                                          : t.frequenzaUnita === 100000002
                                            ? "mesi"
                                            : "anni"}
                                    </span>
                                  )}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        <p className="mt-2 text-xs text-blue-600">
                          Puoi comunque registrare l&apos;intervento senza
                          collegarlo a una scadenza.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            )}

            <Card className="mb-6">
              <h2 className="text-lg font-semibold mb-4">
                Informazioni Generali
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nome Intervento"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  placeholder="Es: Manutenzione caldaia"
                  error={errors.nome}
                  required
                />

                <div>
                  <Input
                    label="Data Intervento"
                    name="dataIntervento"
                    type="date"
                    value={formData.dataIntervento}
                    onChange={handleChange}
                    error={errors.dataIntervento}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Quando e stato effettivamente eseguito l'intervento.
                    {formData.scadenzaId &&
                      " La prossima scadenza sara calcolata da questa data."}
                  </p>
                </div>

                <Select
                  label="RSA"
                  name="rsaId"
                  value={formData.rsaId}
                  onChange={handleChange}
                  options={rsaList.map((r) => ({ value: r.id, label: r.nome }))}
                  placeholder="Seleziona RSA..."
                  error={errors.rsaId}
                  required
                  disabled={!!formData.scadenzaId} // Disabilita se viene da scadenza
                />

                {isAdmin && (
                  <div>
                    <Select
                      label="Fornitore"
                      name="fornitoreId"
                      value={formData.fornitoreId}
                      onChange={handleChange}
                      options={filteredFornitori.map((f) => ({
                        value: f.id,
                        label: f.nome,
                      }))}
                      placeholder={
                        !formData.rsaId
                          ? "Prima seleziona una RSA..."
                          : loadingFornitori
                            ? "Caricamento..."
                            : "Seleziona fornitore..."
                      }
                      error={errors.fornitoreId}
                      required
                      helper={
                        !formData.rsaId
                          ? "Seleziona prima una RSA per vedere i fornitori associati"
                          : filteredFornitori.length > 0 &&
                              filteredFornitori.length < fornitoriList.length
                            ? `${filteredFornitori.length} fornitori associati a questa RSA`
                            : ""
                      }
                      disabled={
                        !!formData.scadenzaId ||
                        !formData.rsaId ||
                        loadingFornitori
                      }
                    />
                  </div>
                )}

                <Select
                  label="Esito *"
                  name="esito"
                  value={formData.esito}
                  onChange={handleChange}
                  options={esitoOptions}
                  placeholder="Seleziona esito..."
                  error={errors.esito}
                  required
                />

                <Select
                  label="Tipo Manutenzione"
                  name="tipoManutenzione"
                  value={formData.tipoManutenzione}
                  onChange={handleChange}
                  options={tipoManutenzioneOptions}
                  required
                />

                {contrattiList.length > 0 && (
                  <Select
                    label="Contratto di Riferimento"
                    name="contrattoId"
                    value={formData.contrattoId}
                    onChange={handleChange}
                    options={contrattiList.map((c) => ({
                      value: c.id,
                      label: c.nome || `Contratto ${c.fornitore?.nome || ""}`,
                    }))}
                    placeholder="Seleziona contratto (opzionale)..."
                  />
                )}
              </div>
            </Card>

            {/* Tipologie Intervento del Fornitore */}
            {tipologieFornitore.length > 0 && (
              <Card className="mb-6">
                <h2 className="text-lg font-semibold mb-2">
                  Tipologie Intervento
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  Seleziona le tipologie di intervento eseguite. Puoi
                  selezionarne piu di una anche con frequenze diverse: alla
                  validazione verra creata una scadenza per ogni frequenza.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {tipologieFornitore.map((tip) => (
                    <label
                      key={tip.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        tipologieSelezionate.includes(tip.id)
                          ? "border-primary-500 bg-primary-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={tipologieSelezionate.includes(tip.id)}
                        onChange={() => handleTipologiaToggle(tip.id)}
                        className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">
                          {tip.nome}
                        </span>
                        {tip.descrizione && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {tip.descrizione}
                          </p>
                        )}
                        {tip.frequenzaValore && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            Frequenza: {tip.frequenzaValore}{" "}
                            {tip.frequenzaUnita === 100000000
                              ? "giorni"
                              : tip.frequenzaUnita === 100000001
                                ? "settimane"
                                : tip.frequenzaUnita === 100000002
                                  ? "mesi"
                                  : tip.frequenzaUnita === 100000003
                                    ? "anni"
                                    : ""}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>

                {tipologieSelezionate.length > 0 && (
                  <p className="text-sm text-primary-600 mt-3">
                    {tipologieSelezionate.length} tipologia/e selezionata/e
                  </p>
                )}
              </Card>
            )}

            <Card className="mb-6">
              <h2 className="text-lg font-semibold mb-4">Dettagli Tecnici</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <Input
                  label="Nome Tecnico"
                  name="nomeTecnico"
                  value={formData.nomeTecnico}
                  onChange={handleChange}
                  placeholder="Nome del tecnico"
                />

                <Input
                  label="Ore Lavorate"
                  name="oreLavorate"
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData.oreLavorate}
                  onChange={handleChange}
                  placeholder="Es: 2.5"
                  error={errors.oreLavorate}
                />

                <Input
                  label="Costo Intervento"
                  name="costoIntervento"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.costoIntervento}
                  onChange={handleChange}
                  placeholder="Es: 150.00"
                  error={errors.costoIntervento}
                  icon={<span className="text-gray-500">€</span>}
                />
              </div>

              <Textarea
                label="Descrizione Intervento"
                name="descrizione"
                value={formData.descrizione}
                onChange={handleChange}
                placeholder="Descrivi l'intervento eseguito, le problematiche riscontrate e le soluzioni adottate..."
                rows={4}
                error={errors.descrizione}
                required
              />

              <Textarea
                label="Materiali Utilizzati"
                name="materialiUtilizzati"
                value={formData.materialiUtilizzati}
                onChange={handleChange}
                placeholder="Elenca i materiali e i ricambi utilizzati..."
                rows={3}
                className="mt-4"
              />
            </Card>

            <Card className="mb-6">
              <h2 className="text-lg font-semibold mb-4">Documentazione</h2>

              <FileUpload
                label="Rapportini / Documentazione"
                accept=".pdf,.jpg,.jpeg,.png,.zip,.rar"
                maxSize={100}
                multiple={true}
                value={formData.rapportini}
                onChange={handleFileChange}
                helper="Carica uno o piu rapportini di intervento (PDF, immagini, ZIP, RAR - max 100MB ciascuno)"
              />
            </Card>

            {/* Info box per fornitori */}
            {isFornitore && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-blue-500 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="text-sm text-blue-800 font-medium">Nota</p>
                    <p className="text-sm text-blue-700 mt-1">
                      L'intervento registrato sara in attesa di validazione da
                      parte del responsabile RSA o RSPP. Riceverai una notifica
                      quando sara approvato.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between items-center">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/interventi")}
                disabled={loading}
              >
                ← Torna alla lista
              </Button>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setFormData({
                      nome: "",
                      dataIntervento: new Date().toISOString().split("T")[0],
                      rsaId: "",
                      fornitoreId: "",
                      tipologiaInterventoId: "",
                      scadenzaId: "",
                      contrattoId: "",
                      esito: "100000000",
                      tipoManutenzione: "100000000",
                      oreLavorate: "",
                      costoIntervento: "",
                      nomeTecnico: "",
                      descrizione: "",
                      materialiUtilizzati: "",
                      rapportini: [],
                    });
                    setErrors({});
                  }}
                  disabled={loading}
                >
                  Reset
                </Button>
                <Button type="submit" loading={loading}>
                  Registra Intervento
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default NuovoInterventoPage;
